/**
 * The per-workspace RUN timeline (migration 127): runs interleaved with the refusals that stopped
 * runs from ever starting.
 *
 * WHY A TIMELINE HERE. The question this answers is sequential - "the apply at 14:03 was refused,
 * and the attestation at 14:19 is why the 14:20 one worked". A table sorts that into columns and
 * loses the causality; a timeline keeps it. One workspace, one chain of events.
 *
 * WHY IT LEADS WITH A BAR CHART. The rail alone carried sequence but not RATE: twenty applies in
 * an hour and twenty across a month drew the same twenty rows, and the reader had to read every
 * timestamp to tell them apart. RunTimelineChart counts the same entries into fixed windows on a
 * real time axis, and the rail below it becomes the detail for ONE window - selected by clicking
 * a bar, or the most recent events until one is. The reasons, actors and attestation links only
 * live here.
 *
 * WHY THE ORG-WIDE FEED IS NOT THIS. It used to be, and that was the mistake: this file argued
 * that the org feed "has no causal chain" and then rendered it on the same rail anyway, so unrelated
 * events across different workspaces, modules and providers LOOKED sequential. The org feed is now
 * a table in PolicyDecisionsPanel, where columns let two rows be compared - which is most of what
 * a ledger is for. Nothing in this file is shared with it beyond the data type.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { formatCount, formatDateTimeSeconds, formatDuration } from '@/lib/format'
import { RunTimelineChart } from '@/features/state/components/RunTimelineChart'
import { bucketLabel, bucketTimeline } from '@/features/state/lib/runTimeline'
import type { PolicyDecision, PolicyInteraction, TimelineEntry } from '@/lib/apiTypes'

/** Human labels for the interaction enum. Short, because they sit on one line with the subject. */
const INTERACTION_LABEL: Record<PolicyInteraction, string> = {
  'state.lock': 'apply refused',
  'state.push': 'state blocked',
  'state.attest': 'attested',
  'state.serve_override': 'enforcement changed',
  'plan.push': 'plan blocked',
  'plan.approve': 'plan approved',
  'module.download': 'module refused',
  'provider.download': 'provider refused',
  'state.pull': 'state read refused',
}


function verdictBadge(verdict: string): { cls: string; text: string } {
  if (verdict === 'block') return { cls: 'badge-red', text: 'BLOCKED' }
  if (verdict === 'override') return { cls: 'badge-amber', text: 'OVERRIDE' }
  return { cls: 'badge-neutral', text: 'WARN' }
}

/** `terraform 1.9.5`, `opentofu 1.8.2`, or nothing when the client did not identify itself. */
function toolLabel(tool: string | null, version: string | null): string | null {
  if (!tool || tool === 'other') return version ? `client ${version}` : null
  return version ? `${tool} ${version}` : tool
}

function Dot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        // -23 centres the 9px dot on the rail's own border (18px of padding plus the 1px line),
        // which is what the --bg halo below is for. At -5 it sat on the first digit of the
        // timestamp and clipped it: "29 Oct" read as "9 Oct".
        position: 'absolute', left: -23, top: 6, width: 9, height: 9, borderRadius: '50%',
        background: color, boxShadow: '0 0 0 3px var(--bg)',
      }}
    />
  )
}

function Rail({ children }: { children: ReactNode }) {
  return (
    <div style={{ borderLeft: '1px solid var(--border)', marginLeft: 6, paddingLeft: 18 }}>
      {children}
    </div>
  )
}

function Entry({ color, children }: { color: string; children: ReactNode }) {
  return (
    <div style={{ position: 'relative', paddingBottom: 16 }}>
      <Dot color={color} />
      {children}
    </div>
  )
}

const META: React.CSSProperties = { color: 'var(--text3)', fontSize: '0.72rem' }
const REASON: React.CSSProperties = {
  margin: '4px 0 0', fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5,
  overflowWrap: 'anywhere',
}

/** One decision on the run rail. The subject is not shown: on a workspace page it is implied. */
export function DecisionEntry({ d }: { d: PolicyDecision }) {
  const badge = verdictBadge(d.verdict)
  const tool = toolLabel(d.tool, d.tool_version)
  const color = d.verdict === 'block' ? 'var(--red)' : 'var(--amber)'
  return (
    <Entry color={color}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.74rem', color: 'var(--text3)' }}>
          {formatDateTimeSeconds(d.decided_at)}
        </span>
        <strong style={{ fontSize: '0.82rem' }}>{INTERACTION_LABEL[d.interaction] ?? d.interaction}</strong>
        <span className={`badge ${badge.cls}`} style={{ fontSize: '0.58rem' }}>{badge.text}</span>
      </div>

      {d.reason && <p style={REASON}>{d.reason}</p>}

      <div style={{ ...META, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {d.policy_name
          ? <span>policy <strong>{d.policy_name}</strong></span>
          // No policy row means the builtin threshold gate or an explicit admin action - saying so
          // is more useful than an empty slot, because it tells the reader which knob to go turn.
          : <span>{d.source === 'builtin' ? 'built-in gate' : d.source === 'manual' ? 'admin action' : d.source}</span>}
        {d.actor_name && <span>{d.actor_name}</span>}
        {tool && <span>{tool}</span>}
        {d.source === 'opa' && <span>custom Rego</span>}
      </div>
    </Entry>
  )
}

const RUN_STATUS: Record<string, { text: string; color: string }> = {
  completed: { text: 'pushed',     color: 'var(--green)' },
  no_push:   { text: 'no change',  color: 'var(--text3)' },
  active:    { text: 'in flight',  color: 'var(--accent)' },
  abandoned: { text: 'abandoned',  color: 'var(--amber)' },
}

/** `OperationTypeApply` -> `apply`. Terraform's own spelling is noise on a timeline. */
function operationLabel(op: string): string {
  const stripped = op.replace(/^OperationType/, '')
  return stripped ? stripped.charAt(0).toLowerCase() + stripped.slice(1) : op
}

function RunEntry({ r }: { r: Extract<TimelineEntry, { kind: 'run' }> }) {
  const status = RUN_STATUS[r.status] ?? { text: r.status, color: 'var(--text3)' }
  return (
    <Entry color={status.color}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.74rem', color: 'var(--text3)' }}>
          {formatDateTimeSeconds(r.at)}
        </span>
        <strong style={{ fontSize: '0.82rem' }}>{operationLabel(r.operation)}</strong>
        <span style={{ ...META, color: status.color }}>{status.text}</span>
        {r.serial != null && <span style={META}>serial {r.serial}</span>}
      </div>
      <div style={{ ...META, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {r.who && <span>{r.who}</span>}
        {r.push_duration_ms != null && <span>push {formatDuration(r.push_duration_ms)}</span>}
        {r.hold_duration_ms != null && <span>lock held {formatDuration(r.hold_duration_ms)}</span>}
        {/* The plan this apply was attested against - the one link that turns two separate
            artefacts into one story. */}
        {r.plan_version_id && <span>attested against a plan</span>}
      </div>
    </Entry>
  )
}

/** How much of the rail is shown before a window is picked. Enough to answer "what just
 *  happened" without the scroll the full feed used to cost. */
const RECENT = 8

/**
 * The per-workspace run timeline: the bar chart, and the rail for whichever window is selected.
 *
 * Selection is held here rather than in the chart because the rail is the thing it changes, and
 * an index is only meaningful against the buckets it was taken from - a refetch that shortens the
 * feed invalidates it, so it is bounds-checked on every render rather than cleared in an effect.
 */
export function RunTimeline({ entries }: { entries: TimelineEntry[] }) {
  const [selected, setSelected] = useState<number | null>(null)
  const { buckets, step } = useMemo(() => bucketTimeline(entries), [entries])

  if (entries.length === 0) {
    return (
      <p style={{ color: 'var(--text3)', fontSize: '0.82rem', margin: 0 }}>
        No runs or policy decisions recorded for this workspace yet. A run appears here the first
        time terraform or tofu takes a lock on it; a decision appears when a policy refuses one.
      </p>
    )
  }

  // One bucket means every entry landed inside the same window - the narrowest the ladder has.
  // There is no shape to draw and no second window to select, so the chart would be a single dot
  // floating in 140px of nothing. The rail alone is the better answer.
  if (buckets.length < 2) {
    return (
      <Rail>
        {entries.map((e) => e.kind === 'run'
          ? <RunEntry key={`run-${e.id}`} r={e} />
          : <DecisionEntry key={`dec-${e.id}`} d={e} />)}
      </Rail>
    )
  }

  const selectedIdx = selected !== null && selected < buckets.length ? selected : null
  const picked = selectedIdx !== null ? buckets[selectedIdx]! : null
  const shown = picked ? picked.entries : entries.slice(0, RECENT)

  return (
    <div>
      {/* 34, not the usual 14: the chart's hover tooltip is anchored above the plot
          (translateY(-100%)), so without reserved room it lands outside the card. */}
      <div style={{ marginTop: 34 }}>
        <RunTimelineChart buckets={buckets} step={step} selected={selectedIdx} onSelect={setSelected} />
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
          flexWrap: 'wrap', margin: '20px 0 14px', paddingTop: 14, borderTop: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>
          {picked
            ? <>
                <strong>{bucketLabel(picked, step)}</strong>
                {' · '}{formatCount(picked.entries.length)}
                {picked.entries.length === 1 ? ' event' : ' events'}
              </>
            : <>
                <strong>{entries.length > RECENT ? `${RECENT} most recent events` : 'All events'}</strong>
                {entries.length > RECENT && ' · click a bar above for one window'}
              </>}
        </div>
        {picked && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>
            show recent
          </button>
        )}
      </div>

      {shown.length === 0
        ? <p style={{ color: 'var(--text3)', fontSize: '0.78rem', margin: 0 }}>
            Nothing in this window - no run took a lock and no policy refused one.
          </p>
        : <Rail>
            {shown.map((e) => e.kind === 'run'
              ? <RunEntry key={`run-${e.id}`} r={e} />
              : <DecisionEntry key={`dec-${e.id}`} d={e} />)}
          </Rail>}
    </div>
  )
}

